import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, waLink } from "@/lib/api";
import { MessageCircle, Loader2 } from "lucide-react";

const occasions = [
  "Corporate Event",
  "Wedding",
  "Engagement",
  "Birthday",
  "Baby Shower / Announcement",
  "Anniversary",
  "Just Because",
];

const inputCls =
  "border-0 border-b border-[#311B4D] rounded-none bg-transparent px-0 text-[#FDFBF7] placeholder:text-[#6b5a86] focus-visible:ring-0 focus-visible:border-[#D4AF37] transition-colors";

export default function EnquirySection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    occasion: "",
    quantity: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! We'll reach out to craft your perfect gift.");
      setForm({
        name: "",
        phone: "",
        email: "",
        occasion: "",
        quantity: "",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enquire"
      className="relative overflow-hidden py-24 md:py-32"
      data-testid="enquiry"
    >
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:grid-cols-2 md:px-12">
        <div>
          <Reveal>
            <Overline>Let&apos;s Create Together</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif text-4xl font-light leading-[1.05] md:text-6xl">
              Let&apos;s craft your{" "}
              <span className="gold-gradient-text italic">perfect gift</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#A894C2]">
              Planning an event or a special surprise? Share a few details and
              our team will personally design a gifting experience for you —
              bulk and bespoke orders welcome.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href={waLink("Hi Elixz! I'd like to plan a gifting order.")}
              target="_blank"
              rel="noreferrer"
              data-testid="enquiry-whatsapp"
              className="pulse-glow mt-9 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-[#0F0616] transition-transform duration-300 hover:scale-[1.03]"
            >
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <motion.form
            onSubmit={submit}
            className="rounded-2xl border border-[#311B4D] bg-[#140823] p-8 md:p-10"
            data-testid="enquiry-form"
          >
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                  Your Name *
                </Label>
                <Input
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Ananya"
                  className={inputCls}
                  data-testid="enquiry-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="grid gap-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                    Phone *
                  </Label>
                  <Input
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="WhatsApp number"
                    className={inputCls}
                    data-testid="enquiry-phone"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                    Email
                  </Label>
                  <Input
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Optional"
                    className={inputCls}
                    data-testid="enquiry-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="grid gap-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                    Occasion
                  </Label>
                  <Select
                    value={form.occasion}
                    onValueChange={(v) => setForm((f) => ({ ...f, occasion: v }))}
                  >
                    <SelectTrigger
                      className={inputCls + " h-10"}
                      data-testid="enquiry-occasion"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="border-[#311B4D] bg-[#1A0B2E] text-[#FDFBF7]">
                      {occasions.map((o) => (
                        <SelectItem key={o} value={o} className="focus:bg-[#311B4D]">
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                    Quantity
                  </Label>
                  <Input
                    value={form.quantity}
                    onChange={set("quantity")}
                    placeholder="e.g. 50 boxes"
                    className={inputCls}
                    data-testid="enquiry-quantity"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A894C2]">
                  Message
                </Label>
                <Textarea
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your event or gifting idea..."
                  rows={3}
                  className={inputCls + " resize-none"}
                  data-testid="enquiry-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiry-submit"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-semibold text-[#0F0616] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  "Send Enquiry"
                )}
              </button>
            </div>
          </motion.form>
        </Reveal>
      </div>
    </section>
  );
}
